/**
 * User Preferences Validation Schemas
 *
 * Zod schemas for validating user preferences-related forms and API requests.
 * Provides client-side validation that matches server-side validation rules.
 */

import { z } from './zod';
import {
  createEnumFromOptions,
  THEME_SELECTION_OPTIONS,
  MEASUREMENT_SYSTEM_OPTIONS,
  LOCALE_OPTIONS,
  DASHBOARD_PROFILE_OPTIONS,
  TRAINER_DASHBOARD_PROFILE_OPTIONS,
  PREFERRED_WORKOUT_DAYS_OPTIONS,
} from '../utils/select-options';

// =============================================================================
// ENUM SCHEMAS FROM OPTIONS
// =============================================================================

/**
 * Theme selection enum schema
 * Values: 'light' | 'dark' | 'auto'
 */
export const themeSelectionSchema = createEnumFromOptions(
  THEME_SELECTION_OPTIONS
);

/**
 * Measurement system enum schema
 * Values: 'metric' | 'imperial'
 */
export const measurementSystemSchema = createEnumFromOptions(
  MEASUREMENT_SYSTEM_OPTIONS
);

/**
 * Language enum schema
 * Values from LOCALE_OPTIONS (en, es, fr, de, it, pt, ru, ja, ko, zh)
 * Note: LOCALE_OPTIONS includes an empty string option for "Select language (optional)"
 */
const nonEmptyLocaleOptions = LOCALE_OPTIONS.filter(
  (option) => option.value !== ''
);
export const languageSchema = createEnumFromOptions(nonEmptyLocaleOptions);

/**
 * Dashboard profile enum schema for clients
 * Values: 'default' | 'social' | 'analytics' | 'compact'
 */
const clientDashboardOptions = DASHBOARD_PROFILE_OPTIONS;

/**
 * Dashboard profile enum schema for trainers
 * Values: 'trainer'
 */
const trainerDashboardOptions = TRAINER_DASHBOARD_PROFILE_OPTIONS;

/**
 * Combined dashboard profile enum schema
 * Values: 'default' | 'social' | 'analytics' | 'compact' | 'trainer'
 */
const allDashboardOptions = [
  ...clientDashboardOptions,
  ...trainerDashboardOptions,
];
export const dashboardProfileSchema =
  createEnumFromOptions(allDashboardOptions);

/**
 * Preferred workout day enum schema
 * Values: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'
 */
export const preferredWorkoutDaySchema = createEnumFromOptions(
  PREFERRED_WORKOUT_DAYS_OPTIONS
);

// =============================================================================
// COMPLEX FIELD SCHEMAS
// =============================================================================

/**
 * Workouts per week validation schema
 * Range: 1-7 (inclusive)
 */
export const workoutsPerWeekSchema = z
  .number()
  .int('Workouts per week must be a whole number')
  .min(1, 'Workouts per week must be at least 1')
  .max(7, 'Workouts per week cannot exceed 7')
  .nullable();

/**
 * Preferred workout days array validation schema
 * Array of WorkoutDay values
 */
export const preferredWorkoutDaysSchema = z
  .array(preferredWorkoutDaySchema)
  .max(7, 'Cannot select more than 7 days')
  .default([]);

/**
 * Notification preferences validation schema
 * Record of boolean values for different notification types
 */
export const notificationPreferencesSchema = z.record(z.string(), z.boolean());

/**
 * Privacy settings validation schema
 * Record of string or boolean values for different privacy settings
 */
export const privacySettingsSchema = z.record(
  z.string(),
  z.union([z.string(), z.boolean()])
);

/**
 * The account's timezone as a device detected it: an IANA identifier such as
 * `America/Vancouver`, backward-compatible names (`US/Pacific`, `Etc/GMT+5`)
 * included. Never an offset — `-07:00` is a distance from UTC, not a place
 * with its own daylight-saving rules. The API checks the name against PHP's
 * identifiers; this checks only its shape.
 */
export const detectedTimezoneSchema = z
  .string()
  .trim()
  .min(1, 'A timezone is required')
  .max(64, 'That timezone name is too long')
  .regex(
    /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*$/,
    'Use a timezone name such as America/Vancouver'
  );

// =============================================================================
// USER PREFERENCES VALIDATION SCHEMA
// =============================================================================

/**
 * Complete user preferences validation schema
 * Matches the UserPreferences structure in the User type
 */
export const userPreferencesSchema = z.object({
  theme_selection: themeSelectionSchema,
  measurement_system: measurementSystemSchema,
  language: languageSchema,
  dashboard_profile: dashboardProfileSchema,
  workouts_per_week: workoutsPerWeekSchema,
  preferred_workout_days: preferredWorkoutDaysSchema,
  notification_preferences: notificationPreferencesSchema,
  privacy_settings: privacySettingsSchema,
  detected_timezone: detectedTimezoneSchema.nullable().optional(),
});

/**
 * Partial user preferences schema for updates
 * All fields are optional for PATCH requests.
 * zod 4 applies a default even inside .partial(), so the field's default is
 * unwrapped here: a key the caller leaves out stays out of the update.
 */
export const updateUserPreferencesSchema = userPreferencesSchema
  .extend({ preferred_workout_days: preferredWorkoutDaysSchema.unwrap() })
  .partial();

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type UserPreferencesInput = z.input<typeof userPreferencesSchema>;
export type UserPreferencesOutput = z.output<typeof userPreferencesSchema>;
export type UpdateUserPreferencesInput = z.input<
  typeof updateUserPreferencesSchema
>;
export type UpdateUserPreferencesOutput = z.output<
  typeof updateUserPreferencesSchema
>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

/**
 * Validate complete user preferences data
 * @param data - The data to validate
 * @returns The validated and typed user preferences
 * @throws ZodError if validation fails
 */
export function validateUserPreferences(data: unknown): UserPreferencesOutput {
  return userPreferencesSchema.parse(data);
}

/**
 * Safely validate complete user preferences data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUserPreferences(data: unknown) {
  return userPreferencesSchema.safeParse(data);
}

/**
 * Validate partial user preferences data for updates
 * @param data - The data to validate
 * @returns The validated and typed partial user preferences
 * @throws ZodError if validation fails
 */
export function validateUpdateUserPreferences(
  data: unknown
): UpdateUserPreferencesOutput {
  return updateUserPreferencesSchema.parse(data);
}

/**
 * Safely validate partial user preferences data for updates
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateUserPreferences(data: unknown) {
  return updateUserPreferencesSchema.safeParse(data);
}

/**
 * Validate a single theme selection value
 * @param theme - The theme value to validate
 * @returns boolean indicating if the value is valid
 */
export function isValidThemeSelection(theme: unknown): boolean {
  return themeSelectionSchema.safeParse(theme).success;
}

/**
 * Validate a single measurement system value
 * @param system - The measurement system value to validate
 * @returns boolean indicating if the value is valid
 */
export function isValidMeasurementSystem(system: unknown): boolean {
  return measurementSystemSchema.safeParse(system).success;
}

/**
 * Validate a single language value
 * @param language - The language value to validate
 * @returns boolean indicating if the value is valid
 */
export function isValidLanguage(language: unknown): boolean {
  return languageSchema.safeParse(language).success;
}

/**
 * Validate a single dashboard profile value
 * @param profile - The dashboard profile value to validate
 * @returns boolean indicating if the value is valid
 */
export function isValidDashboardProfile(profile: unknown): boolean {
  return dashboardProfileSchema.safeParse(profile).success;
}

/**
 * Validate a single workout day value
 * @param day - The workout day value to validate
 * @returns boolean indicating if the value is valid
 */
export function isValidWorkoutDay(day: unknown): boolean {
  return preferredWorkoutDaySchema.safeParse(day).success;
}

/**
 * Validate workouts per week value
 * @param count - The workouts per week value to validate
 * @returns boolean indicating if the value is valid
 */
export function isValidWorkoutsPerWeek(count: unknown): boolean {
  return workoutsPerWeekSchema.safeParse(count).success;
}
